import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  AuthService,
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  ModalComponent,
  nameof,
  SapphireBlueButton,
  SingleSelect,
  ToastService,
} from "@mindmemobile/mm-shared-global";
import PurchaseLinkVariantTable from "./purchase-link/components/PurchaseLinkVariantTable";
import PurchaseLinkAdvancedSection from "./purchase-link/components/PurchaseLinkAdvancedSection";
import PurchaseLinkRelatedItems from "./purchase-link/components/PurchaseLinkRelatedItems";
import {
  ItemDetailPageModel,
  RelatedItemsModel,
  RelatedSectionModel,
  MerchantAccount,
  SellerModel,
  PurchaseLinkModel,
  PaymentMethodDetails,
} from "../shared/models";
import { PurchaseLinkStore } from "../shared/store";
import {
  MerchantAccountService,
  PurchaseLinkService,
  SellerService,
} from "../shared/services";
import {
  ApplicationStatus,
  PurchaseLinkPriceOption,
  PurchaseVariantSelectionType,
  SellerReturnPolicyType,
  SellerTermsAndConditionsType,
  SystemItemTypes,
} from "../shared/enums";
import {
  getVariantOptions,
  HalfContainer,
  priceOptions,
  PurchaseLinkPopupWrapper,
} from "../shared/styles";
import {
  CarouselComponent,
  NavPills,
  SurchargePercentField,
} from "../shared/components";

type IProps = {
  purchaseLinkStore: PurchaseLinkStore;
  onClose: (refresh: boolean) => void;
  isEdit?: boolean;
};

const promotionOptions = [
  { label: "None", value: "None" } as IOption,
  { label: "All", value: "All" } as IOption,
];

const sellerTermsAndConditionsOptions: IOption[] = Object.keys(
  SellerTermsAndConditionsType,
).map((key) => {
  return {
    label:
      SellerTermsAndConditionsType[
        key as keyof typeof SellerTermsAndConditionsType
      ],
    value: key,
  };
});

const sellerReturnPolicyOptions: IOption[] = Object.keys(
  SellerReturnPolicyType,
).map((key) => {
  return {
    label: SellerReturnPolicyType[key as keyof typeof SellerReturnPolicyType],
    value: key,
  };
});

const PurchaseLinkPopUp: React.FC<IProps> = ({
  purchaseLinkStore,
  onClose,
  isEdit,
}) => {
  const { purchaseLinkModel } = purchaseLinkStore;
  /* A donation link collects donations from donors; a link for any other item
     type takes purchases from customers. It also shows only two list options,
     which the layout below accounts for. */
  const isDonationLink =
    purchaseLinkModel.itemType === SystemItemTypes.Donation;
  const [activeNavPill, setActiveNavPill] = useState("details");

  useEffect(() => {
    getMerchantAccounts();
    loadSellers();
  }, []);

  /* Editing a saved link: record its seller and policy choices before anything can reset them, so
     a deliberate "Require Acceptance" survives even when the seller's copy was emptied later. Not
     done when creating - a new link has no earlier choice, so it just follows the seller. */
  useEffect(() => {
    /* Waits for the id: on the first render the popup is already in edit mode but the link has not
       arrived, and capturing then would record the empty defaults - which the capture's
       first-call-wins guard would keep, hiding the very choice this exists to preserve. */
    if (isEdit && purchaseLinkStore.purchaseLinkModel.id) {
      purchaseLinkStore.captureSellerTermsBaseline?.();
    } else if (!isEdit) {
      /* The store is a singleton, so a previously edited link would otherwise leave its baseline
         behind and this new link would inherit its dropdowns. */
      purchaseLinkStore.clearSellerTermsBaseline?.();
    }
  }, [isEdit, purchaseLinkStore.purchaseLinkModel.id]);

  /* Re-runs when the seller changes or the seller list reloads, so a policy the
     seller cannot show is put back to "Do Not Display" rather than saved. */
  const selectedSellerDetails = purchaseLinkStore.selectedSellerDetails;
  useEffect(() => {
    /* The seller list this resolves from carries no policy copy, so the copy is
       fetched per seller before anything is decided from it. */
    purchaseLinkStore.loadSelectedSellerPolicies?.();
    purchaseLinkStore.applySellerTermsAvailability?.();
  }, [
    selectedSellerDetails?.id,
    selectedSellerDetails?.hasTermsAndConditions,
    selectedSellerDetails?.hasReturnPolicy,
    purchaseLinkStore.isSelectedSellerPolicyResolved,
  ]);

  const getMerchantAccounts = () => {
    MerchantAccountService.getMerchantAccounts(
      AuthService.authData.accountId,
      1,
      10000,
      ""
    ).subscribe((response) => {
      if (response.result) {
        const activeMerchantAccounts = (
          response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
          []
        ).filter((f) => f.applicationStatus === ApplicationStatus.Active);

        purchaseLinkStore.updateMerchantAccounts(activeMerchantAccounts);
        /* No merchant account saved on the link means it is being created, so
           the account defaulted to below counts as a fresh selection and its
           surcharge defaults apply. A saved link keeps what it stored, except
           for a missing rate: a link the API returned without a
           surchargePercentage (never set, or zeroed by a save made with the
           surcharge off) keeps the selected account's rate so the field is
           never empty. Its own "Add surcharge" and "Ask donor first" are left
           alone. */
        const isNewSelection =
          purchaseLinkModel.selectedMerchantAccount.name === "";
        const defaultAccount = activeMerchantAccounts[0];
        const selectedAccount = isNewSelection
          ? defaultAccount
          : purchaseLinkStore.selectedMerchantAccountWithSettings;
        const accountDefaults = selectedAccount?.surchargeDefaults();
        const keepsAccountRate =
          !!accountDefaults &&
          parseFloat(accountDefaults.surchargePercentage || "0") > 0 &&
          purchaseLinkStore.purchaseLinkModel.surchargePercentageValue <= 0;
        const surchargeDefaults = isNewSelection
          ? accountDefaults || {}
          : keepsAccountRate
            ? { surchargePercentage: accountDefaults!.surchargePercentage }
            : {};
        purchaseLinkStore.updatePurchaseLink(
          new PurchaseLinkModel({
            ...purchaseLinkStore.purchaseLinkModel,
            selectedMerchantAccount: isNewSelection
              ? defaultAccount
              : purchaseLinkStore.purchaseLinkModel.selectedMerchantAccount,
            ...surchargeDefaults,
          })
        );
      }
    });
  };

  function loadSellers() {
    SellerService.getSellerNames(AuthService.authData.accountId).subscribe(
      (response) => {
        if (response.result) {
          const activeSellers = (
            response.result?.map((m) => SellerModel.deserializeAPIModel(m)) ||
            []
          ).filter((f) => f.isActive === true);

          if (activeSellers.length === 0) return;

          purchaseLinkStore.updateSellers(activeSellers);
          purchaseLinkStore.updatePurchaseLink(
            new PurchaseLinkModel({
              ...purchaseLinkStore.purchaseLinkModel,
              selectedSeller:
                purchaseLinkStore.purchaseLinkModel.selectedSeller?.id?.length >
                0
                  ? purchaseLinkStore.purchaseLinkModel.selectedSeller
                  : activeSellers[0],
            })
          );
        }
      }
    );
  }

  /**
   * Surcharge values that should come from the merchant account rather than
   * from the field being edited:
   *
   * - picking a merchant account seeds all three from that account;
   * - switching "Add surcharge" on with no rate yet fills in the account's
   *   rate and "ask donor first", so editing an existing link shows the
   *   merchant defaults instead of an empty box.
   *
   * The link owns the copy from here on, so a later edit to the merchant
   * account never rewrites a link that was already saved.
   */
  const merchantSurchargeDefaults = (
    field: keyof PurchaseLinkModel,
    value: any
  ): Partial<PurchaseLinkModel> => {
    if (field === "selectedMerchantAccount" && value instanceof MerchantAccount) {
      return value.surchargeDefaults();
    }
    if (field === "surchargeEnabled" && value === true) {
      if (purchaseLinkModel.surchargePercentageValue > 0) return {};
      const defaults =
        purchaseLinkStore.selectedMerchantAccountWithSettings.surchargeDefaults();
      return {
        surchargePercentage: defaults.surchargePercentage,
        surchargeAskCustomerFirst: defaults.surchargeAskCustomerFirst,
      };
    }
    return {};
  };

  const handleInputChange = (field: keyof PurchaseLinkModel, value: any) => {
    purchaseLinkStore.updatePurchaseLink(
      new PurchaseLinkModel({
        ...purchaseLinkModel,
        [field]: value,
        ...merchantSurchargeDefaults(field, value),
      })
    );
  };

  const handleItemDetailInputChange = (
    field: keyof ItemDetailPageModel,
    value: string | boolean | number
  ) => {
    purchaseLinkStore.updatePurchaseLink(
      new PurchaseLinkModel({
        ...purchaseLinkModel,
        itemDetails: new ItemDetailPageModel({
          ...purchaseLinkModel.itemDetails,
          [field]: value,
        }),
      })
    );
  };

  const textInput = (
    field: keyof PurchaseLinkModel | keyof ItemDetailPageModel,
    type: "text" | "number",
    value: string | number,
    placeHolder: string,
    className: string,
    error: boolean,
    errorMessage: string,
    onChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void
  ) => {
    return (
      <Input
        type={type}
        value={value}
        error={error}
        errorMessage={errorMessage}
        className={`styled-input merchant-inputs ${className}`}
        onChange={onChange}
        placeholder={placeHolder}
      />
    );
  };

  function checkBox(
    value: boolean,
    fieldName: keyof PurchaseLinkModel | keyof ItemDetailPageModel,
    label: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  ) {
    return (
      <Input
        value={value}
        id={`chk${fieldName}`}
        type="checkbox"
        label={label}
        onChange={onChange}
      />
    );
  }

  function singleSelect(
    selectedKey: string,
    fieldName: keyof PurchaseLinkModel,
    options: IOption[],
    hasError: boolean,
    onChange: (option: IOption) => void,
    disabled?: boolean
  ) {
    return (
      <SingleSelect
        hasError={hasError}
        selectedKey={selectedKey}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options]}
        onChange={onChange}
        disabled={disabled}
        className="purchase-link-select"
      />
    );
  }

  const handleCheckboxChange = (variantId: string, isChecked: boolean) => {
    const updatedVariantIds = isChecked
      ? [...purchaseLinkModel.selectedVariantIds, variantId]
      : purchaseLinkModel.selectedVariantIds.filter((id) => id !== variantId);
    handleInputChange("selectedVariantIds", updatedVariantIds);
  };

  const handlePriceCheckboxChange = (priceId: string, isChecked: boolean) => {
    const updatedPriceIds = isChecked
      ? [...purchaseLinkModel.selectedPriceIds, priceId]
      : purchaseLinkModel.selectedPriceIds.filter((id) => id !== priceId);
    handleInputChange("selectedPriceIds", updatedPriceIds);
  };

  const renderVariantsOrPrice = () => {
    const variantOptions = getVariantOptions(
      purchaseLinkModel.isTrackInventory
    );
    if (purchaseLinkModel.itemType === SystemItemTypes.Product) {
      return (
        <div>
          <CustomLabel>Variants</CustomLabel>
          {singleSelect(
            purchaseLinkModel.itemDetails?.hasVariants
              ? purchaseLinkModel.variantsType
              : variantOptions[1].value,
            "variantsType",
            variantOptions,
            purchaseLinkModel.hasError(
              nameof<PurchaseLinkModel>(
                "variantsType" as keyof PurchaseLinkModel
              )
            ),
            (option) => handleInputChange("variantsType", option.value),
            !purchaseLinkModel.itemDetails?.hasVariants
          )}
          {purchaseLinkModel.variants &&
            purchaseLinkModel.variantsType ===
              PurchaseVariantSelectionType.SpecificVariants && (
              <PurchaseLinkVariantTable
                showOnHand={purchaseLinkModel.variants.showOnHand}
                showPriceByVariant={purchaseLinkModel.variants.priceByVariant}
                variants={purchaseLinkModel.variants.options}
                selectedVariantIds={purchaseLinkModel.selectedVariantIds}
                handleCheckboxChange={handleCheckboxChange}
                priceDetails={
                  purchaseLinkModel.pricingInputs &&
                  purchaseLinkModel.pricingInputs[0]
                }
              />
            )}
        </div>
      );
    } else if (purchaseLinkModel.itemType !== SystemItemTypes.Donation) {
      return (
        <div>
          <CustomLabel>Price</CustomLabel>
          {singleSelect(
            purchaseLinkModel.priceType,
            "priceType",
            priceOptions,
            purchaseLinkModel.hasError(
              nameof<PurchaseLinkModel>("priceType" as keyof PurchaseLinkModel)
            ),
            (option) => handleInputChange("priceType", option.value),
            false
          )}
          {purchaseLinkModel.priceType === PurchaseLinkPriceOption.Specific && (
            <FlexContainer
              fxLayout="column"
              spacing={10}
              padding="15px 0px 0px"
            >
              {purchaseLinkModel.pricingInputs?.map((price) => {
                const isChecked = purchaseLinkModel.selectedPriceIds.includes(
                  price.id
                );
                const isActive = price.isActive;
                return (
                  <div key={price.id}>
                    <FlexContainer alignItems="center">
                      <Input
                        value={isChecked}
                        id={`chk${price.id}`}
                        type="checkbox"
                        label=""
                        onChange={(e) =>
                          handlePriceCheckboxChange(price.id, e.target.checked)
                        }
                        disabled={!isActive}
                      />
                      <CustomLabel className="pl-no-margin">
                        {price.name}
                      </CustomLabel>
                    </FlexContainer>
                  </div>
                );
              })}
            </FlexContainer>
          )}
        </div>
      );
    }
  };

  const renderLinkDetailsSection = () => {
    if (!purchaseLinkModel.itemDetails) return;
    return (
      <>
        <CustomLabel className="pl-header">Link Details</CustomLabel>
        <FlexContainer
          fxLayout="column"
          spacing={30}
          className="pl-input-labels"
        >
          <div>
            <CustomLabel>Name</CustomLabel>
            {textInput(
              "name",
              "text",
              purchaseLinkModel.itemDetails?.name,
              "Enter value",
              "",
              purchaseLinkModel.hasError(
                nameof<PurchaseLinkModel>("name" as keyof PurchaseLinkModel)
              ),
              "",
              (e) => handleItemDetailInputChange("name", e.target.value)
            )}
          </div>
          {purchaseLinkModel.itemType !== SystemItemTypes.Donation &&
            renderVariantsOrPrice()}
          {purchaseLinkModel.itemType !== SystemItemTypes.Donation && (
            <div>
              <CustomLabel>Promotion</CustomLabel>
              {singleSelect(
                purchaseLinkModel.promotionValue,
                "promotionValue",
                promotionOptions,
                purchaseLinkModel.hasError(
                  nameof<PurchaseLinkModel>(
                    "promotionValue" as keyof PurchaseLinkModel
                  )
                ),
                (option) => handleInputChange("promotionValue", option.value),
                false
              )}
            </div>
          )}
        </FlexContainer>
      </>
    );
  };
  const renderListOptions = () => {
    if (!purchaseLinkModel.itemDetails) return;
    return (
      <CollapsiblePanel title="List Options" className="pl-chkbx-panel">
        <FlexContainer
          alignItems="center"
          spacing={15}
          fxWrap="wrap"
          className={`pl-chkbx-flex${isDonationLink ? " pl-chkbx-stacked" : ""}`}
        >
          {checkBox(
            purchaseLinkModel.itemDetails?.tax,
            "tax",
            "Allow Tax",
            (e) => handleItemDetailInputChange("tax", e.target.checked)
          )}
          {checkBox(
            purchaseLinkModel.includeRelatedItems,
            "includeRelatedItems",
            "Include Related Items",
            (e) => handleInputChange("includeRelatedItems", e.target.checked)
          )}
          {purchaseLinkModel.itemType !== SystemItemTypes.Donation && (
            <>
              {checkBox(
                purchaseLinkModel.chooseQuantity,
                "chooseQuantity",
                "Customer chooses quantity",
                (e) => handleInputChange("chooseQuantity", e.target.checked)
              )}
              {checkBox(
                purchaseLinkModel.showOriginalPrice || false,
                "showOriginalPrice",
                "Show Original Price",
                (e) => handleInputChange("showOriginalPrice", e.target.checked)
              )}
              <FlexContainer spacing={10} alignItems="center">
                {checkBox(
                  purchaseLinkModel.itemDetails?.return || false,
                  "return",
                  "Allow Returns",
                  (e) => handleItemDetailInputChange("return", e.target.checked)
                )}
                {purchaseLinkModel.itemDetails?.return && (
                  <>
                    <div className="return-days-input">
                      {textInput(
                        "returnDays",
                        "number",
                        purchaseLinkModel.itemDetails?.returnDays || 0,
                        "Enter value",
                        "",
                        purchaseLinkModel.hasError(
                          nameof<PurchaseLinkModel>(
                            "returnDays" as keyof PurchaseLinkModel
                          )
                        ),
                        "",
                        (e) =>
                          handleItemDetailInputChange(
                            "returnDays",
                            e.target.value
                          )
                      )}
                    </div>
                    <CustomLabel>days</CustomLabel>
                  </>
                )}
              </FlexContainer>
            </>
          )}

          {purchaseLinkModel.itemType === SystemItemTypes.Service ||
            (purchaseLinkModel.itemType === SystemItemTypes.Membership && (
              <>
                <div>
                  <FlexContainer spacing={10} alignItems="center">
                    {checkBox(
                      purchaseLinkModel.freeTrial,
                      "freeTrial",
                      "Free trial",
                      (e) => handleInputChange("freeTrial", e.target.checked)
                    )}
                    {purchaseLinkModel.freeTrial && (
                      <>
                        {" "}
                        <div className="return-days-input">
                          {textInput(
                            "freeTrialDays",
                            "number",
                            purchaseLinkModel.freeTrialDays,
                            "Enter value",
                            "",
                            purchaseLinkModel.hasError(
                              nameof<PurchaseLinkModel>(
                                "freeTrialDays" as keyof PurchaseLinkModel
                              )
                            ),
                            "",
                            (e) =>
                              handleInputChange("freeTrialDays", e.target.value)
                          )}
                        </div>
                        <CustomLabel>days</CustomLabel>
                      </>
                    )}
                  </FlexContainer>
                </div>
              </>
            ))}
        </FlexContainer>
      </CollapsiblePanel>
    );
  };

  /* Terms and the return policy are only offered for a seller that actually has
     that copy, matching the checkout builder. Anything the seller is missing is
     forced back to "Do Not Display" by the store, so nothing stale is saved. */
  const renderTermsSection = () => {
    const seller = purchaseLinkStore.selectedSellerDetails;
    /* Shown when the seller has the copy, or when this link already carries a choice - so a saved
       "Require Acceptance" stays visible and editable even after the seller's copy is emptied. Safe
       from flicker only because applySellerTermsAvailability now exempts the seller the link was
       loaded with; it resets on a seller switch, which flips these to false in the same pass that
       clears the value. */
    const showSellerTerms =
      !!seller?.hasTermsAndConditions ||
      purchaseLinkStore.sellerTermsWasConfigured;
    const showSellerReturnPolicy =
      !!seller?.hasReturnPolicy ||
      purchaseLinkStore.sellerReturnPolicyWasConfigured;
    if (!showSellerTerms && !showSellerReturnPolicy) {
      return null;
    }
    return (
      <div className="pl-terms-section">
        <CustomLabel
          fontSize="18px"
          fontWeight="600"
          lineHeight="24px"
          margin="0px 0px 8px"
        >
          Terms
        </CustomLabel>
        <CustomLabel
          fontSize="14px"
          fontWeight="400"
          lineHeight="20px"
          color="#667085"
          margin="0px 0px 24px"
        >
          Choose how {seller?.name || "this seller"}&apos;s policies are
          presented at checkout.
        </CustomLabel>
        {showSellerTerms && (
          <div className="pl-terms-row">
            <CustomLabel className="pl-terms-label">
              Terms &amp; Conditions
            </CustomLabel>
            {singleSelect(
              purchaseLinkModel.sellerTermsAndConditions,
              "sellerTermsAndConditions",
              sellerTermsAndConditionsOptions,
              false,
              (option: IOption) =>
                handleInputChange(
                  "sellerTermsAndConditions",
                  option.value as keyof typeof SellerTermsAndConditionsType,
                ),
            )}
          </div>
        )}
        {showSellerReturnPolicy && (
          <div className="pl-terms-row">
            <CustomLabel className="pl-terms-label">Return Policy</CustomLabel>
            {singleSelect(
              purchaseLinkModel.sellerReturnPolicy,
              "sellerReturnPolicy",
              sellerReturnPolicyOptions,
              false,
              (option: IOption) =>
                handleInputChange(
                  "sellerReturnPolicy",
                  option.value as keyof typeof SellerReturnPolicyType,
                ),
            )}
          </div>
        )}
      </div>
    );
  };

  const checkRelatedItems = (
    relatedSectionData: RelatedSectionModel | null
  ): boolean | null => {
    if (!relatedSectionData) return null;

    const { includedItems, recommendedItems, requiredItems } =
      relatedSectionData;

    const checkItemsBasedOnFlag = (relatedItems: RelatedItemsModel) => {
      if (relatedItems.showSelectItems) {
        return relatedItems.items.length > 0;
      } else if (relatedItems.showCategories) {
        return relatedItems.categories.length > 0;
      } else if (relatedItems.showAccountCredits) {
        return parseFloat(relatedItems.accountCredits) > 0;
      }
      return false;
    };

    return (
      (includedItems && checkItemsBasedOnFlag(includedItems)) ||
      (recommendedItems && checkItemsBasedOnFlag(recommendedItems)) ||
      (requiredItems && checkItemsBasedOnFlag(requiredItems))
    );
  };

  const navPills = () => {
    const hasRelatedItems = checkRelatedItems(
      purchaseLinkModel.relatedSectionData || null
    );
    if (hasRelatedItems) {
      return (
        <FlexContainer
          alignItems="center"
          padding="0px 0px 30px"
          className="pl-navpills"
        >
          <div className="flex-auto-grow">
            <NavPills
              defaultActiveKey="details"
              pills={[
                {
                  key: "details",
                  title: "DETAILS",
                },
                {
                  key: "related-items",
                  title: "RELATED ITEMS",
                },
              ]}
              onSelect={(key) => setActiveNavPill(key)}
            />
          </div>
        </FlexContainer>
      );
    }
  };

  // const renderMedia = (media: MediaModel) => {
  //     switch (media.type) {
  //         case MediaTypes.Video:
  //             return <Video url={media.fullURL} />;
  //         default:
  //             return (
  //                 <img src={media.fullURL} style={{ height: '150px', width: '100px' }} className="product-image" />
  //             );
  //     }
  // };

  const renderPurchaseLinkContent = () => {
    return (
      <FlexContainer fxLayout="column">
        {navPills()}
        {activeNavPill === "details" ? (
          <>
            <div className="width-inherit">
              <FlexContainer>
                <HalfContainer> {renderLinkDetailsSection()}</HalfContainer>
                <HalfContainer>
                  {" "}
                  <CarouselComponent
                    media={purchaseLinkModel.itemDetails?.medias || []}
                    className={isDonationLink ? "pl-donation-carousel" : ""}
                  />
                  {/* {purchaseLinkModel.itemDetails?.medias &&
                                      purchaseLinkModel.itemDetails?.medias?.length > 0 &&
                                      purchaseLinkModel.itemDetails?.medias.map((media, index) => (
                                          <FlexContainer key={index} size={0} justifyContent="center">
                                              {renderMedia(media)}
                                          </FlexContainer>
                                      ))} */}
                </HalfContainer>
              </FlexContainer>
            </div>

            {renderListOptions()}
            <div className="pl-surcharge-section">
              <CustomLabel
                fontSize="18px"
                fontWeight="600"
                lineHeight="24px"
                margin="0px 0px 8px"
              >
                Surcharge
              </CustomLabel>
              <CustomLabel
                fontSize="14px"
                fontWeight="400"
                lineHeight="20px"
                color="#667085"
                margin="0px 0px 24px"
              >
                Charge a fee to cover processing costs.
              </CustomLabel>
              <FlexContainer alignItems="center" spacing={15} fxWrap="wrap">
                {checkBox(
                  purchaseLinkModel.surchargeEnabled,
                  "surchargeEnabled",
                  "Add surcharge",
                  (e) =>
                    handleInputChange("surchargeEnabled", e.target.checked)
                )}
                {purchaseLinkModel.surchargeEnabled && (
                  <>
                    <SurchargePercentField
                      value={purchaseLinkModel.surchargePercentage}
                      hasError={purchaseLinkModel.hasError(
                        "surchargePercentage" as keyof PurchaseLinkModel
                      )}
                      onChange={(percentage) =>
                        handleInputChange("surchargePercentage", percentage)
                      }
                    />
                    <CustomLabel>
                      to every {isDonationLink ? "donation" : "purchase"}
                    </CustomLabel>
                    {checkBox(
                      purchaseLinkModel.surchargeAskCustomerFirst,
                      "surchargeAskCustomerFirst",
                      isDonationLink ? "Ask donor first" : "Ask customer first",
                      (e) =>
                        handleInputChange(
                          "surchargeAskCustomerFirst",
                          e.target.checked
                        )
                    )}
                  </>
                )}
              </FlexContainer>
              {purchaseLinkModel.surchargeEnabled &&
                purchaseLinkModel.hasError(
                  "surchargePercentage" as keyof PurchaseLinkModel
                ) && (
                  <CustomLabel
                    fontSize="13px"
                    fontWeight="500"
                    color="#ef4444"
                    margin="12px 0px 0px"
                  >
                    {purchaseLinkModel.errors.find(
                      (e) => e.field === "surchargePercentage"
                    )?.message || PaymentMethodDetails.surchargeRangeMessage}
                  </CustomLabel>
                )}
            </div>
            <PurchaseLinkAdvancedSection
              sellers={purchaseLinkStore.sellers}
              handleInputChange={(
                field: keyof PurchaseLinkModel,
                value: MerchantAccount | IOption
              ) => handleInputChange(field, value)}
              selectedMerchantAccount={
                purchaseLinkModel.selectedMerchantAccount
              }
              selectedSeller={purchaseLinkModel.selectedSeller}
              merchantAccounts={purchaseLinkStore.merchantAccounts}
              merchantHasError={purchaseLinkModel.hasError(
                nameof<PurchaseLinkModel>(
                  "selectedMerchantAccount" as keyof PurchaseLinkModel
                )
              )}
              sellerHasError={purchaseLinkModel.hasError(
                nameof<PurchaseLinkModel>(
                  "selectedSeller" as keyof PurchaseLinkModel
                )
              )}
            />
            {/* Below Advanced: the Terms options describe the seller picked in that
                section, so they read in order rather than referring ahead to it. */}
            {renderTermsSection()}
          </>
        ) : (
          <PurchaseLinkRelatedItems
            relatedSectionData={purchaseLinkModel.relatedSectionData}
          />
        )}
      </FlexContainer>
    );
  };

  const onClickSave = async () => {
    try {
      const isValidate = purchaseLinkModel.validate();
      if (!isValidate) {
        purchaseLinkStore.updatePurchaseLink(
          new PurchaseLinkModel({ ...purchaseLinkModel })
        );
        return;
      }

      const apiModel = purchaseLinkModel.createAPIModel();
      let response;

      if (isEdit) {
        response =
          await PurchaseLinkService.updatePurchaseLink(apiModel).toPromise();
      } else {
        if (purchaseLinkModel.isNew) {
          response =
            await PurchaseLinkService.createPurchaseLink(apiModel).toPromise();
        } else {
          response =
            await PurchaseLinkService.updatePurchaseLink(apiModel).toPromise();
        }
      }

      if (response?.result) {
        onClose(true);
        if (isEdit) {
          ToastService.showSuccess("Purchase link updated successfully!");
        } else {
          if (purchaseLinkModel.isNew) {
            ToastService.showSuccess("Purchase link created successfully!");
          } else {
            ToastService.showSuccess("Purchase link updated successfully!");
          }
        }
      } else {
        ToastService.showError("Error in creating purchase link");
      }
    } catch (error) {
      ToastService.showError("Error Occurred");
    }
  };

  return (
    <PurchaseLinkPopupWrapper>
      {purchaseLinkModel.isOpen && (
        <ModalComponent
          title={`${purchaseLinkModel.itemDetails?.name} - Purchase Link`}
          titleFontSize="22px"
          bodyPadding="40px"
          showFooter={true}
          minHeight="300px"
          wrapperWidth="1000px"
          bodyBackgroundColor="#fefefe"
          footerBackground="#F4F7FB"
          footerHeight="auto"
          onClose={() => onClose(false)}
          footerContent={
            <FlexContainer
              alignItems="center"
              justifyContent="center"
              spacing={15}
            >
              <SapphireBlueButton
                minWidth="120px"
                height="40px"
                onClick={() => onClose(false)}
              >
                Close
              </SapphireBlueButton>
              <SapphireBlueButton
                primary
                minWidth="120px"
                height="40px"
                onClick={() => onClickSave()}
              >
                Save
              </SapphireBlueButton>
            </FlexContainer>
          }
        >
          {renderPurchaseLinkContent()}
        </ModalComponent>
      )}
    </PurchaseLinkPopupWrapper>
  );
};
export default observer(PurchaseLinkPopUp);
